export const weekDays = [
  { value: "MONDAY", number: 1, short: "mo" },
  { value: "TUESDAY", number: 2, short: "di" },
  { value: "WEDNESDAY", number: 3, short: "mi" },
  { value: "THURSDAY", number: 4, short: "do" },
  { value: "FRIDAY", number: 5, short: "fr" },
  { value: "SATURDAY", number: 6, short: "sa" },
  { value: "SUNDAY", number: 7, short: "so" },
]

export function convertWeekDayNames(weekDayNames) {
  if (weekDayNames) {
    return weekDays.filter(day => weekDayNames.some(val => val === day.value))
  } else {
    return []
  }
}

/**
 * Inverse of [convertWeekDayNames]: the enum names the API stores, from the objects the UI works
 * with. Idempotent on purpose — it takes a name as readily as an object, so running it twice over
 * the same array (or over one the store never converted) cannot degrade it.
 *
 * Anything it does not recognise is dropped rather than passed through: the backend maps this onto
 * a Kotlin `List<WEEKDAYS>`, and Jackson rejects a null element there with a 400 that names no
 * field, so a stray entry must never reach the wire.
 */
export function toWeekDayNames(workDays) {
  if (!workDays) return []
  return workDays
    .map(day => (day !== null && typeof day === "object" ? day.value : day))
    .filter(name => weekDays.some(day => day.value === name))
}

/**
 * `Date#getDay()` counts 0 = Sunday … 6 = Saturday; `weekDays[].number` is ISO-8601, 1 = Monday …
 * 7 = Sunday. The two agree on Monday through Saturday and disagree on exactly one day, so code
 * comparing them directly looks right and silently never matches a Sunday. Deriving the lookup from
 * the table itself keeps the numberings from drifting apart again.
 */
const weekDayNamesByGetDay = weekDays.reduce((names, day) => {
  names[day.number % 7] = day.value
  return names
}, [])

/**
 * Whether [date] falls on one of [workDays].
 *
 * Takes the work days in either shape — the objects the UI works with or the enum names the API
 * stores — so a calendar cell can no longer crash on an array that has not been converted, or has
 * been converted twice. This renders on every visible day of every month the user pages through,
 * which is the last place that should be picky about its input.
 *
 * @param workDays {Array | undefined | null}
 * @param date {Date | String | Number}
 */
export function isWorkDayDate(workDays, date) {
  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) return false
  return toWeekDayNames(workDays).includes(weekDayNamesByGetDay[parsed.getDay()])
}
